import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, type TableColumn, type TableAction } from "@/components/Table";
import { Icon } from "@/components/Icon";
import { ModalDelete } from "@/components";
import { EditCompanyModal } from "@/pages/Companies/EditCompanyModal";
import type { CompanyItem } from "@/pages/Companies/types";
import { tenantApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

import { useAuth } from "@/context/AuthContext";

interface CompaniesTableProps {
    items: CompanyItem[];
    loading: boolean;
    search: string;
    onSearchChange: (value: string) => void;
    onUpdateItem: (item: CompanyItem) => void;
    onDeleteItem: (id: number) => void;
    onRefresh: () => void;
    className?: string;
    error?: string | null;
}
export const CompaniesTable = ({
    items,
    loading,
    search,
    onSearchChange,
    onUpdateItem,
    onDeleteItem,
    onRefresh,
    error,
    className = "",
}: CompaniesTableProps) => {
    const { t } = useTranslation("global");
    const { isAdmin } = useAuth();
    const { showSuccess, showError } = useToast();
    const [deleteTarget, setDeleteTarget] = useState<CompanyItem | null>(null);
    const [editTarget, setEditTarget] = useState<CompanyItem | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            await tenantApi.delete(deleteTarget.id);
            showSuccess(t("companies.deleted"));
            const id = deleteTarget.id;
            setDeleteTarget(null);
            onDeleteItem(id);
        } catch (err) {
            showError(t("companies.error_delete"));
        } finally {
            setIsDeleting(false);
        }
    };

    const tableColumns: TableColumn<CompanyItem>[] = [
        {
            key: "logo_url",
            label: t("companies.logo"),
            sortable: false,
            render: (row) => {
                const logo = row.data?.logo_url || row.logo_url;
                return logo ? (
                    <img
                        src={logo}
                        alt=""
                        className="h-8 w-8 rounded-full object-cover"
                    />
                ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">
                        {(row.data?.name || row.name || "?").charAt(0).toUpperCase()}
                    </div>
                );
            },
        },
        {
            key: "name",
            label: t("companies.name"),
            render: (row) => row.data?.name || row.name,
        },
        {
            key: "database",
            label: t("companies.database_current"),
            render: (row) => row.data?.database || row.database || "",
        },
        {
            key: "vigepra1",
            label: t("companies.database_vigepra1"),
            render: (row) => row.data?.vigepra1 || row.vigepra1 || "",
        },
    ];

    const tableActions: TableAction<CompanyItem>[] = [
        {
            label: t("table.edit"),
            icon: (
                <Icon
                    value="pencil"
                    size={14}
                    className="text-slate-600 dark:text-zinc-300"
                />
            ),
            onClick: (row) => {
                setEditTarget(row);
            },
        },
        {
            label: t("table.delete"),
            icon: <Icon value="trash" size={14} className="text-rose-600" />,
            variant: "delete",
            onClick: (row) => {
                setDeleteTarget(row);
            },
        },
    ];

    return (
        <div className={className}>
            <Table
                viewKey="vigepra_companies"
                viewStorageKey="vigepra_company_views"
                appearance="workspace"
                error={error}
                onRetry={onRefresh}
                columns={tableColumns}
                data={items}
                actions={isAdmin ? tableActions : []}
                actionsPosition="right"
                searchValue={search}
                onSearchChange={onSearchChange}
                loading={loading}
                filterbox={false}
                defaultSortKey="name"
                defaultSortOrder="asc"
            />
            {deleteTarget && (
                <ModalDelete
                    isOpen={!!deleteTarget}
                    onClose={() => !isDeleting && setDeleteTarget(null)}
                    itemName={deleteTarget.data?.name || deleteTarget.name}
                    badgeText={String(deleteTarget.id)}
                    //itemDescription={t('tasks.delete_warning')}
                    onConfirm={handleConfirmDelete}
                    isDeleting={isDeleting}
                />
            )}

            {editTarget && (
                <EditCompanyModal
                    isOpen={!!editTarget}
                    onClose={() => setEditTarget(null)}
                    company={editTarget}
                    onSuccess={(updatedTenant) => {
                        onUpdateItem(updatedTenant);
                    }}
                />
            )}
        </div>
    );
};
